/**
 * 2D Polylines Domain Adapter for Template Repeater
 *
 * Applies 2D affine transforms (position, rotation, uniform scale) to polyline
 * template outputs. Each template's polylines are cloned and transformed per slot,
 * then concatenated into a single flat Polylines array.
 */

import type { RepeaterDomainAdapter, AdapterContext } from '@/nodes/Dependencies';
import type { GraphIndex } from '@/runtime/GraphIndex';
import type { StyledPolyline } from './TurtleNode/TurtleNode';
import { getCyclic } from '@/nodes/arrayUtils';
import { logger } from '@/logging';

interface Vec3 { x: number; y: number; z: number }
type Polylines = StyledPolyline[];

export function createPolylineAdapter(): RepeaterDomainAdapter {
  return {
    outputTypes: ['polylines'],

    getTransformEndpoints() {
      return {
        posX:     { value: [], dataType: 'array', label: 'Position X' },
        posY:     { value: [], dataType: 'array', label: 'Position Y' },
        rotation: { value: [], dataType: 'array', label: 'Rotation' },
        scale:    { value: [], dataType: 'array', label: 'Scale' },
      };
    },

    getOptionEndpoints() {
      return {
        rotationUnit: { value: 'radians', dataType: 'string', label: 'Rotation Unit', options: ['radians', 'degrees'] },
      };
    },

    collectTemplateOutput(
      graphIndex: GraphIndex,
      templateId: string,
      outputPaths: { endpointName: string; internalNodeId: string }[]
    ): Polylines {
      const allPolylines: StyledPolyline[] = [];

      for (const { internalNodeId } of outputPaths) {
        const intKey = graphIndex.nodeKey(templateId, internalNodeId);
        const gn = graphIndex.getNodeByKey(intKey);
        if (!gn) continue;

        const node = gn.node as any;
        if (typeof node.getPolylines === 'function') {
          const polylines = node.getPolylines() as Polylines;
          if (Array.isArray(polylines)) {
            for (const pl of polylines) {
              const xs = pl.points.map((p: Vec3) => p.x);
              const ys = pl.points.map((p: Vec3) => p.y);
              const w = Math.max(...xs) - Math.min(...xs);
              const h = Math.max(...ys) - Math.min(...ys);
              logger.trace('polylineAdapter', `collectTemplate ${templateId}: ${w.toFixed(1)}×${h.toFixed(1)} fill=${pl.fill}`);
            }
            allPolylines.push(...polylines);
          }
        }
      }

      return allPolylines;
    },

    buildInstances(
      templateOutputs: { output: Polylines; slotIndices: number[] }[],
      transforms: Record<string, any[]>,
      _context: AdapterContext,
      options?: Record<string, any>,
    ): any[] {
      const t0 = performance.now();
      const result = transformAll(templateOutputs, transforms, options);
      const t1 = performance.now();
      logger.info('polylineAdapter', `buildInstances: ${result.length} polylines in ${(t1 - t0).toFixed(1)}ms`);
      return result;
    },

    updateInstanceTransforms(
      existingObjects: any[],
      templateOutputs: { output: Polylines; slotIndices: number[] }[],
      transforms: Record<string, any[]>,
      options?: Record<string, any>,
    ): void {
      const rebuilt = transformAll(templateOutputs, transforms, options);
      // Mutate the array in-place so the Repeater's reference stays valid
      existingObjects.length = 0;
      existingObjects.push(...rebuilt);
    },

    hitTest(objects: any[], dataX: number, dataY: number, radius: number): number {
      const r2 = radius * radius;
      let bestDist = r2;
      let bestIndex = -1;

      // Iterate in reverse so topmost (latest drawn) instance wins ties
      for (let p = objects.length - 1; p >= 0; p--) {
        const polyline = objects[p] as StyledPolyline;
        if (polyline.instanceIndex === undefined) continue;
        const pts = polyline.points;

        // Filled polygons: check if point is inside
        if (polyline.fill && pointInPolygon(dataX, dataY, pts)) {
          return polyline.instanceIndex;
        }

        // Stroke-based: check distance to edges
        for (let i = 0; i < pts.length - 1; i++) {
          const dist = sqDistToSegment(dataX, dataY, pts[i], pts[i + 1]);
          if (dist < bestDist) {
            bestDist = dist;
            bestIndex = polyline.instanceIndex;
          }
        }
      }

      return bestIndex;
    },
  };
}

/** Ray-casting point-in-polygon test (2D, ignores z) */
function pointInPolygon(px: number, py: number, pts: Vec3[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const yi = pts[i].y, yj = pts[j].y;
    const xi = pts[i].x, xj = pts[j].x;
    if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** Squared distance from point (px,py) to line segment (a→b) */
function sqDistToSegment(px: number, py: number, a: Vec3, b: Vec3): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 === 0) { const dx = px - a.x; const dy = py - a.y; return dx * dx + dy * dy; }
  const t = Math.max(0, Math.min(1, ((px - a.x) * abx + (py - a.y) * aby) / len2));
  const dx = px - (a.x + t * abx);
  const dy = py - (a.y + t * aby);
  return dx * dx + dy * dy;
}

/** Apply 2D affine transforms to all template polylines for all slots */
function transformAll(
  templateOutputs: { output: Polylines; slotIndices: number[] }[],
  transforms: Record<string, any[]>,
  options?: Record<string, any>,
): StyledPolyline[] {
  const posX = transforms.posX ?? [];
  const posY = transforms.posY ?? [];
  const rotation = transforms.rotation ?? [];
  const scale = transforms.scale ?? [];
  const useDegrees = options?.rotationUnit !== 'radians';

  const result: StyledPolyline[] = [];

  for (const { output: templatePolylines, slotIndices } of templateOutputs) {
    if (!templatePolylines || templatePolylines.length === 0) continue;

    for (const globalIdx of slotIndices) {
      const px = getCyclic(posX, globalIdx, 0);
      const py = getCyclic(posY, globalIdx, 0);
      const rRaw = getCyclic(rotation, globalIdx, 0);
      const s = getCyclic(scale, globalIdx, 1);

      const rRad = useDegrees ? rRaw * (Math.PI / 180) : rRaw;
      const cosR = Math.cos(rRad);
      const sinR = Math.sin(rRad);

      for (const polyline of templatePolylines) {
        const transformed: Vec3[] = new Array(polyline.points.length);
        for (let i = 0; i < polyline.points.length; i++) {
          const p = polyline.points[i];
          transformed[i] = {
            x: p.x * s * cosR - p.y * s * sinR + px,
            y: p.x * s * sinR + p.y * s * cosR + py,
            z: p.z,
          };
        }
        result.push({ points: transformed, color: polyline.color, fill: polyline.fill, lineWidth: polyline.lineWidth, instanceIndex: globalIdx });
      }
    }
  }

  return result;
}

